import React from 'react';
import { Link } from 'react-router-dom';
import { useFeaturedProducts } from '../../hooks/useProducts';
import { extractPlainText } from '../../utils/richTextRenderer';
import { getImageUrl } from '../../services/api';

const FeaturedProducts = () => {
  const { products: featuredProducts, loading, error } = useFeaturedProducts();

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-catalina-blue"></div>
            <p className="mt-4 text-gray-600">Loading featured products...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-red-600">
            <p>Error loading products: {error}</p>
          </div>
        </div>
      </section>
    );
  }

  if (!featuredProducts || featuredProducts.length === 0) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-gray-600">
            <p>No featured products available at the moment.</p>
          </div>
        </div>
      </section>
    );
  }

  // Define the order of products
  const productOrder = [
    'Telescopic Ladders',
    'Platform Trolleys',
    'Manual & Electric Stackers and HPT',
    'Shopping Trolleys',
    'Shopping Market Racks And commercial Racks',
    'Safety Equipment'
  ];

  // Sort featured products based on the defined order
  const sortedProducts = [...featuredProducts].sort((a, b) => {
    const indexA = productOrder.findIndex(name => 
      a.Title?.toLowerCase().includes(name.toLowerCase())
    );
    const indexB = productOrder.findIndex(name => 
      b.Title?.toLowerCase().includes(name.toLowerCase())
    );

    // Products matching the order come first, sorted by order
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-catalina-blue mb-4">Featured Products</h2>
          <p className="text-lg text-gray-600">
            Discover our most popular industrial equipment solutions
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {sortedProducts.map((product, index) => {
            const imageUrl = product.Image ? getImageUrl(product.Image, 'medium') : null;
            
            return (
              <div
                key={product.id}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition duration-300"
              >
                {/* Product Image */}
                {imageUrl ? (
                  <div className="w-full h-48 bg-white overflow-hidden">
                    <img 
                      src={imageUrl} 
                      alt={product.Image?.alternativeText || product.Title}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                    <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
                
                {/* Product Content */}
                <div className="p-6">
                  <div className="flex items-start mb-4">
                    <span className="text-2xl font-bold text-catalina-blue mr-3">{index + 1}.</span>
                    <h3 className="text-xl font-bold text-gray-900 flex-1">{product.Title}</h3>
                  </div>
                  {product.Description && (
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {extractPlainText(product.Description, 70)}
                    </p>
                  )}
                  <Link
                    to={`/product/${product.slug}`}
                    className="inline-flex items-center text-catalina-blue font-semibold hover:text-san-marino transition duration-300"
                  >
                    View Details
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProducts;